import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "./hooks/useLocalizedPath";

// Lazy load all pages for performance
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const TransfersPage = lazy(() => import("./pages/TransfersPage"));
const FleetPage = lazy(() => import("./pages/FleetPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ToursPage = lazy(() => import("./pages/ToursPage"));
const TourDetailPage = lazy(() => import("./pages/TourDetailPage"));
const TaxiSalzburgPage = lazy(() => import("./pages/TaxiSalzburgPage"));
const TaxiMuenchenSalzburgPage = lazy(() => import("./pages/TaxiMuenchenSalzburgPage"));
const AirportTransferPage = lazy(() => import("./pages/AirportTransferPage"));
const WeddingTransferPage = lazy(() => import("./pages/WeddingTransferPage"));
const SeminarTransferPage = lazy(() => import("./pages/SeminarTransferPage"));
const CityTransferPage = lazy(() => import("./pages/CityTransferPage"));

// New SEO pages
const TransferServicePage = lazy(() => import("./pages/TransferServicePage"));
const TaxiKostenPage = lazy(() => import("./pages/TaxiKostenPage"));
const SkiTransfersPage = lazy(() => import("./pages/SkiTransfersPage"));
const WhatsAppBookingPage = lazy(() => import("./pages/WhatsAppBookingPage"));
const FestspielePage = lazy(() => import("./pages/FestspielePage"));
const AusflugszielePage = lazy(() => import("./pages/AusflugszielePage"));
const TaxiStadtPage = lazy(() => import("./pages/TaxiStadtPage"));
const VIPFamilyPage = lazy(() => import("./pages/VIPFamilyPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const CitiesPage = lazy(() => import("./pages/CitiesPage"));
const CityDetailPage = lazy(() => import("./pages/CityDetailPage"));

const queryClient = new QueryClient();

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Detects browser language and redirects bare paths to /:lang/... */
const LangRedirect = () => {
  const location = useLocation();

  const saved = localStorage.getItem('salzburg-taxi-lang');
  const browserLang = navigator.language?.split('-')[0];
  const targetLang = (saved && SUPPORTED_LANGS.includes(saved))
    ? saved
    : (browserLang && SUPPORTED_LANGS.includes(browserLang))
      ? browserLang
      : DEFAULT_LANG;

  const path = location.pathname === '/' ? '' : location.pathname;
  return <Navigate to={`/${targetLang}${path}`} replace />;
};

/** Validates :lang param is a supported language, otherwise redirects */
const LangGuard = () => {
  const { lang } = useParams();
  const location = useLocation();

  if (!lang || !SUPPORTED_LANGS.includes(lang)) {
    // Invalid lang prefix — redirect with detected language
    const saved = localStorage.getItem('salzburg-taxi-lang');
    const browserLang = navigator.language?.split('-')[0];
    const targetLang = (saved && SUPPORTED_LANGS.includes(saved))
      ? saved
      : (browserLang && SUPPORTED_LANGS.includes(browserLang))
        ? browserLang
        : DEFAULT_LANG;

    // Strip the invalid prefix and redirect
    const rest = location.pathname.replace(/^\/[^/]+/, '');
    return <Navigate to={`/${targetLang}${rest || ''}`} replace />;
  }

  return (
    <LanguageProvider>
      <AppRoutes />
    </LanguageProvider>
  );
};

/** All app routes wrapped under /:lang */
const AppRoutes = () => (
  <Routes>
    <Route index element={<Index />} />
    <Route path="services" element={<ServicesPage />} />
    <Route path="services/:slug" element={<ServiceDetailPage />} />
    <Route path="transfers" element={<TransfersPage />} />
    <Route path="tours" element={<ToursPage />} />
    <Route path="tours/:slug" element={<TourDetailPage />} />
    <Route path="taxi-salzburg" element={<TaxiSalzburgPage />} />
    <Route path="taxi-muenchen-salzburg" element={<TaxiMuenchenSalzburgPage />} />
    <Route path="airport-transfer" element={<AirportTransferPage />} />
    <Route path="services/wedding-transfer" element={<WeddingTransferPage />} />
    <Route path="services/seminar-transfer" element={<SeminarTransferPage />} />
    <Route path="transfer/:slug" element={<CityTransferPage />} />
    <Route path="fleet" element={<FleetPage />} />
    <Route path="booking" element={<BookingPage />} />
    <Route path="about" element={<AboutPage />} />
    <Route path="contact" element={<ContactPage />} />
    
    {/* SEO Landing Pages */}
    <Route path="transferservice" element={<TransferServicePage />} />
    <Route path="taxi-kosten" element={<TaxiKostenPage />} />
    <Route path="ski-transfers" element={<SkiTransfersPage />} />
    <Route path="whatsapp-bestellen" element={<WhatsAppBookingPage />} />
    <Route path="salzburger-festspiele-2026" element={<FestspielePage />} />
    <Route path="ausflugsziele" element={<AusflugszielePage />} />
    <Route path="taxi-salzburg-stadt" element={<TaxiStadtPage />} />
    <Route path="vip-familien-chauffeur" element={<VIPFamilyPage />} />
    <Route path="sitemap" element={<SitemapPage />} />
    <Route path="cities" element={<CitiesPage />} />
    <Route path="city/:slug" element={<CityDetailPage />} />
    
    {/* SEO Alias Routes */}
    <Route path="taxi-leistungen" element={<ServicesPage />} />
    <Route path="tagestouren" element={<ToursPage />} />
    <Route path="tagestouren-day-trips" element={<ToursPage />} />
    <Route path="taxi-buchen" element={<BookingPage />} />
    <Route path="taxi-salzburg-booking" element={<BookingPage />} />
    <Route path="hochzeitstransfer-salzburg" element={<WeddingTransferPage />} />
    <Route path="seminar-transfer-planen" element={<SeminarTransferPage />} />
    <Route path="hochzeit-taxi-transfer-planen" element={<WeddingTransferPage />} />
    <Route path="airport-salzburg-transfer" element={<AirportTransferPage />} />
    <Route path="shuttleservice-salzburg-muenchen" element={<TaxiMuenchenSalzburgPage />} />
    <Route path="taxi-standplaetze" element={<TaxiStadtPage />} />
    <Route path="private-touren" element={<ToursPage />} />
    <Route path="vip-ski-transfer" element={<SkiTransfersPage />} />
    <Route path="hotel-skigebiet-transfer" element={<SkiTransfersPage />} />
    
    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Language-prefixed routes with validation */}
              <Route path="/:lang/*" element={<LangGuard />} />
              {/* Redirect bare root to language-prefixed version */}
              <Route path="/" element={<LangRedirect />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
